import { isSupportedGitReferenceUrl } from '@ottocode/sdk/references';
import {
	BookOpen,
	ChevronDown,
	FolderOpen,
	RefreshCw,
	Terminal,
	Trash2,
} from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
	useDeleteReference,
	useReferences,
	useRetryReference,
	useSaveReference,
} from '../../hooks/useReferences';
import type {
	Reference,
	ReferenceScope,
	ReferenceStatus,
} from '../../lib/api-client/references';
import { pickPlatformDirectory } from '../../lib/platform';
import { toast } from '../../stores/toastStore';
import { Button } from '../ui/Button';
import { StableSpinner } from '../ui/StableSpinner';
import { DirectoryBrowserModal } from './DirectoryBrowserModal';
import {
	EntityCheckbox,
	EntityEditor,
	EntityEmptyState,
	EntityField,
	EntityListGroup,
	EntityListPage,
	EntityRow,
	entityMonoInputClass,
	entitySelectClass,
	SegmentedControl,
} from './SettingsEntityPage';

const NAME_PATTERN = /^[a-z0-9][a-z0-9._-]*$/;

type Draft = {
	name: string;
	description: string;
	type: 'git' | 'local';
	location: string;
	ref: string;
	enabled: boolean;
};

const EMPTY_DRAFT: Draft = {
	name: '',
	description: '',
	type: 'git',
	location: '',
	ref: '',
	enabled: true,
};

function toDraft(name: string, reference: Reference): Draft {
	return {
		name,
		description: reference.description,
		type: reference.source.type,
		location:
			reference.source.type === 'git'
				? reference.source.url
				: reference.source.path,
		ref: reference.source.type === 'git' ? (reference.source.ref ?? '') : '',
		enabled: reference.enabled !== false,
	};
}

function ReferenceGitActivity({ status }: { status: ReferenceStatus }) {
	const outputRef = useRef<HTMLPreElement>(null);
	const isCloning = status.status === 'cloning';
	const output = status.output?.length
		? status.output
		: [
				isCloning
					? 'Waiting for Git output...'
					: 'Git clone exited with an error.',
			];

	useEffect(() => {
		const element = outputRef.current;
		if (element) element.scrollTop = element.scrollHeight;
	});

	return (
		<div
			className={`overflow-hidden rounded-md border ${
				isCloning
					? 'border-border/70 bg-muted/20'
					: 'border-red-500/30 bg-red-500/5'
			}`}
		>
			<div className="flex items-center gap-2 border-b border-border/50 px-2.5 py-1.5">
				{isCloning ? (
					<StableSpinner title="Cloning repository" className="h-3 w-3" />
				) : (
					<Terminal className="h-3 w-3 text-red-400" />
				)}
				<span className="text-[11px] font-medium text-foreground">
					{isCloning ? 'Preparing repository' : 'Repository clone failed'}
				</span>
				<span
					className={`ml-auto font-mono text-[9px] uppercase tracking-wide ${
						isCloning ? 'text-emerald-500' : 'text-red-400'
					}`}
				>
					{isCloning ? 'live' : 'failed'}
				</span>
			</div>
			<pre
				ref={outputRef}
				aria-live="polite"
				className="max-h-28 min-h-14 overflow-auto whitespace-pre-wrap break-all bg-background/60 px-2.5 py-2 font-mono text-[10px] leading-relaxed text-muted-foreground"
			>
				{output.join('\n')}
			</pre>
			{status.error ? (
				<p className="border-t border-red-500/20 px-2.5 py-1.5 font-mono text-[10px] text-red-400">
					{status.error}
				</p>
			) : null}
		</div>
	);
}

export function ReferencesSettings() {
	const nameId = useId();
	const typeId = useId();
	const locationId = useId();
	const refId = useId();
	const descriptionId = useId();
	const enabledId = useId();
	const [scope, setScope] = useState<ReferenceScope>('local');
	const referencesQuery = useReferences(scope);
	const saveReference = useSaveReference();
	const retryReference = useRetryReference();
	const deleteReference = useDeleteReference();
	const [selectedName, setSelectedName] = useState('');
	const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
	const [isEditorOpen, setIsEditorOpen] = useState(false);
	const [isBrowserOpen, setIsBrowserOpen] = useState(false);
	const references = referencesQuery.data?.references ?? {};
	const statuses = referencesQuery.data?.statuses ?? {};
	const entries = useMemo(
		() => Object.entries(references).sort(([a], [b]) => a.localeCompare(b)),
		[references],
	);
	const normalizedName = draft.name.trim().toLowerCase();
	const isEditing = selectedName !== '' && selectedName === normalizedName;
	const selectedStatus = selectedName ? statuses[selectedName] : undefined;
	const trimmedLocation = draft.location.trim();
	const gitUrlError =
		draft.type === 'git' &&
		trimmedLocation !== '' &&
		!isSupportedGitReferenceUrl(trimmedLocation)
			? 'Use an HTTP(S) or SSH Git URL. Choose Local directory for filesystem paths.'
			: undefined;
	const isValid =
		NAME_PATTERN.test(normalizedName) &&
		draft.description.trim() !== '' &&
		trimmedLocation !== '' &&
		!gitUrlError;

	function selectReference(name: string, reference: Reference) {
		setSelectedName(name);
		setDraft(toDraft(name, reference));
		setIsEditorOpen(true);
	}

	function createReference() {
		setSelectedName('');
		setDraft({ ...EMPTY_DRAFT, name: 'new-reference' });
		setIsEditorOpen(true);
	}

	function clearDraft() {
		setSelectedName('');
		setDraft(EMPTY_DRAFT);
		setIsEditorOpen(false);
	}

	async function handleBrowse() {
		const request = pickPlatformDirectory();
		if (!request) {
			setIsBrowserOpen(true);
			return;
		}
		try {
			const path = await request;
			if (path) {
				setDraft((current) => ({ ...current, location: path }));
			}
		} catch (error) {
			toast.error(
				error instanceof Error
					? error.message
					: 'Failed to open directory picker',
			);
		}
	}

	async function handleSave() {
		if (!NAME_PATTERN.test(normalizedName)) {
			toast.error(
				'Names can only use lowercase letters, numbers, dots, dashes, and underscores.',
			);
			return;
		}
		if (!draft.description.trim() || !draft.location.trim()) {
			toast.error('Description and source are required.');
			return;
		}
		if (gitUrlError) {
			toast.error(gitUrlError);
			return;
		}
		const source: Reference['source'] =
			draft.type === 'git'
				? {
						type: 'git',
						url: draft.location.trim(),
						...(draft.ref.trim() ? { ref: draft.ref.trim() } : {}),
					}
				: { type: 'local', path: draft.location.trim() };
		try {
			await saveReference.mutateAsync({
				name: normalizedName,
				scope,
				reference: {
					description: draft.description.trim(),
					enabled: draft.enabled,
					source,
				},
			});
			toast.success(
				draft.type === 'git'
					? `Saved ${normalizedName}; cloning repository in the background`
					: `Saved ${normalizedName}`,
			);
			clearDraft();
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : 'Failed to save reference',
			);
		}
	}

	async function handleRetry() {
		if (!selectedName) return;
		try {
			await retryReference.mutateAsync(selectedName);
			toast.success(`Retrying ${selectedName}`);
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : 'Failed to retry reference',
			);
		}
	}

	async function handleDelete() {
		if (!selectedName) return;
		try {
			await deleteReference.mutateAsync({ name: selectedName, scope });
			toast.success(`Deleted ${selectedName} from ${scope} config`);
			clearDraft();
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : 'Failed to delete reference',
			);
		}
	}

	if (isEditorOpen) {
		return (
			<EntityEditor
				backLabel="All references"
				onBack={clearDraft}
				title={isEditing ? `Edit ${selectedName}` : 'New reference'}
				subtitle={`Saves to the ${scope === 'local' ? 'project' : 'global'} config`}
				footerStart={
					isEditing ? (
						<>
							<Button
								variant="ghost"
								size="sm"
								onClick={handleDelete}
								disabled={deleteReference.isPending}
								className="h-7 gap-1 px-2 text-xs text-red-500 hover:text-red-400"
							>
								<Trash2 className="h-3.5 w-3.5" /> Delete
							</Button>
							{draft.type === 'git' && selectedStatus?.status === 'error' ? (
								<Button
									variant="secondary"
									size="sm"
									onClick={handleRetry}
									disabled={retryReference.isPending}
									className="h-7 gap-1 px-2 text-xs"
								>
									<RefreshCw className="h-3.5 w-3.5" />
									{retryReference.isPending ? 'Retrying…' : 'Retry clone'}
								</Button>
							) : null}
						</>
					) : null
				}
				footerEnd={
					<>
						<Button
							variant="ghost"
							size="sm"
							onClick={clearDraft}
							className="h-7 px-2.5 text-xs"
						>
							Cancel
						</Button>
						<Button
							size="sm"
							onClick={handleSave}
							disabled={!isValid || saveReference.isPending}
							className="h-7 px-3 text-xs"
						>
							{saveReference.isPending ? 'Saving…' : 'Save reference'}
						</Button>
					</>
				}
			>
				{isEditing &&
				draft.type === 'git' &&
				selectedStatus &&
				selectedStatus.status !== 'available' ? (
					<ReferenceGitActivity status={selectedStatus} />
				) : null}
				<div className="grid gap-3.5 sm:grid-cols-[minmax(0,1fr)_160px]">
					<EntityField id={nameId} label="Name">
						<input
							id={nameId}
							value={draft.name}
							onChange={(event) =>
								setDraft({ ...draft, name: event.target.value })
							}
							className={entityMonoInputClass}
						/>
					</EntityField>
					<EntityField id={typeId} label="Source type">
						<div className="relative">
							<select
								id={typeId}
								value={draft.type}
								onChange={(event) =>
									setDraft({
										...draft,
										type: event.target.value as Draft['type'],
									})
								}
								className={entitySelectClass}
							>
								<option value="git">Git repository</option>
								<option value="local">Local directory</option>
							</select>
							<ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
						</div>
					</EntityField>
				</div>
				<EntityField
					id={locationId}
					label={draft.type === 'git' ? 'Git URL' : 'Directory path'}
					error={gitUrlError}
				>
					<div className="flex gap-2">
						<input
							id={locationId}
							value={draft.location}
							onChange={(event) =>
								setDraft({ ...draft, location: event.target.value })
							}
							placeholder={
								draft.type === 'git'
									? 'https://github.com/org/repository.git'
									: '~/dev/reference-project'
							}
							className={`${entityMonoInputClass} min-w-0 flex-1`}
						/>
						{draft.type === 'local' ? (
							<Button
								variant="secondary"
								size="sm"
								onClick={handleBrowse}
								className="h-8 shrink-0 gap-1.5 whitespace-nowrap px-2.5 text-xs"
							>
								<FolderOpen className="h-3.5 w-3.5" /> Browse
							</Button>
						) : null}
					</div>
				</EntityField>
				{draft.type === 'git' ? (
					<EntityField id={refId} label="Branch or tag (optional)">
						<input
							id={refId}
							value={draft.ref}
							onChange={(event) =>
								setDraft({ ...draft, ref: event.target.value })
							}
							placeholder="main"
							className={entityMonoInputClass}
						/>
					</EntityField>
				) : null}
				<EntityField id={descriptionId} label="When should Otto refer to this?">
					<textarea
						id={descriptionId}
						value={draft.description}
						onChange={(event) =>
							setDraft({ ...draft, description: event.target.value })
						}
						rows={3}
						placeholder="Use this when working on routing, middleware, or Hono conventions."
						className="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm leading-relaxed text-foreground outline-none transition-colors placeholder:text-muted-foreground/50 focus:border-primary"
					/>
				</EntityField>
				<EntityCheckbox
					id={enabledId}
					checked={draft.enabled}
					onChange={(enabled) => setDraft({ ...draft, enabled })}
				>
					Make this reference available to Otto
				</EntityCheckbox>
				<DirectoryBrowserModal
					isOpen={isBrowserOpen}
					initialPath={
						draft.type === 'local' && draft.location.startsWith('/')
							? draft.location
							: undefined
					}
					onClose={() => setIsBrowserOpen(false)}
					onSelect={(path) => {
						setDraft((current) => ({ ...current, location: path }));
						setIsBrowserOpen(false);
					}}
				/>
			</EntityEditor>
		);
	}

	return (
		<EntityListPage
			toolbar={
				<>
					<SegmentedControl
						value={scope}
						options={[
							{ value: 'local', label: 'Project' },
							{ value: 'global', label: 'Global' },
						]}
						onChange={(value) => setScope(value)}
					/>
					<span className="text-xs text-muted-foreground">
						{entries.length} {entries.length === 1 ? 'reference' : 'references'}
					</span>
				</>
			}
			createLabel="New reference"
			onCreate={createReference}
			hint="The list shows the effective merged references. Saving and deleting affects the selected scope."
		>
			{referencesQuery.isLoading ? (
				<div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
					<StableSpinner title="Loading references" /> Loading…
				</div>
			) : entries.length === 0 ? (
				<EntityEmptyState
					icon={<BookOpen className="h-4 w-4" />}
					title="No references yet"
					description="References give Otto relevant repositories and directories without adding their contents to every prompt."
					actionLabel="New reference"
					onAction={createReference}
				/>
			) : (
				<EntityListGroup>
					{entries.map(([name, reference]) => {
						const status = statuses[name];
						const badge =
							reference.source.type === 'local'
								? 'Local'
								: status?.status === 'cloning'
									? 'Cloning…'
									: status?.status === 'error'
										? 'Clone failed'
										: 'Ready';
						const location =
							reference.source.type === 'git'
								? reference.source.url
								: reference.source.path;
						return (
							<div key={name}>
								<EntityRow
									onClick={() => selectReference(name, reference)}
									title={name}
									badge={badge}
									warning={
										status?.status === 'error'
											? status.error || 'Repository clone failed'
											: undefined
									}
									description={reference.description}
									meta={location}
								/>
								{reference.source.type === 'git' &&
								status &&
								status.status !== 'available' ? (
									<div className="mx-3.5 mb-3">
										<ReferenceGitActivity status={status} />
									</div>
								) : null}
							</div>
						);
					})}
				</EntityListGroup>
			)}
		</EntityListPage>
	);
}
